import { all, select, put, takeLatest, call, delay } from 'redux-saga/effects';
import { LoginActionTypes } from './types';
import {
  setAppUserRole,
  setIsSignOutCompleted,
  resetAppAuthStateFinish
} from 'redux/app/actions';
import { fetchUserSuccess } from 'redux/user/actions';
import {
  fetchAuthFailure,
  fetchAuthRequest,
  fetchAuthSuccess,
  fetchSelectSessionOrgRoleFailure,
  fetchSelectSessionOrgRoleSuccess,
  fetchTermsAcceptanceFailure,
  fetchTermsAcceptanceSuccess,
  fetchOTPCodeSuccess,
  fetchOTPCodeFailure,
  signInForExternalAppSuccess,
  signInForExternalAppFailure,
  signInWithTemporaryCodeFailure,
  signInWithTemporaryCodeSuccess,
  signInWithOTPCodeSuccess,
  signInWithOTPCodeFailure
} from './action';
import Services from 'services';
import { initAppFailure } from 'redux/app/actions';
import { AUTH_ERR_MSG } from 'types';
import {
  setAuthenticated,
  setUserSession,
  endSession
} from 'redux/session/actions';
import { LOCAL_STORAGE_KEYS } from 'types';
import Utils from 'utilities/helper.util';
import _ from 'lodash';

function* handleLogin(request?: any) {
  // const { context, values } = request?.payload;
  yield call(fetchAuthRequest);
  try {
    const auth = yield call(
      Services.auth.loginWithSSO,
      request?.payload?.redirectTo
    );
    yield delay(6000);
    yield put(fetchAuthSuccess(auth));
    // if (context === AUTH_TYPE.BASIC_LOGIN) {
    //   const auth = yield call(Services.auth.loginUser, values);
    //   yield delay(3000);
    //   yield put(fetchAuthSuccess(auth));
    // }
  } catch (err) {
    const errMsg = (err instanceof Error && err.stack) || err;
    yield put(fetchAuthFailure(errMsg));
    yield put(initAppFailure());
  }
}
// User Terms
function* handleUserTerms(request?: any) {
  const { userTermsId } = request?.payload;
  try {
    const termsResponse = yield call(
      Services.auth.fetchUserTermsAcceptanceService,
      userTermsId
    );
    if (termsResponse) {
      const currentUser = yield call(Services.user.fetchUser);
      const currPreferences = currentUser?.preferences;
      const {
        isMarketingUpdatesAllowed,
        isNewslettersAllowed
      } = request?.payload?.preferences;

      let preferences = Utils.isObjectEmpty(currPreferences)
        ? { isMarketingUpdatesAllowed, isNewslettersAllowed }
        : {
            ...currPreferences,
            isMarketingUpdatesAllowed,
            isNewslettersAllowed
          };
      if (!currPreferences?.timezone) {
        const timezone = Utils.getTimeZone();
        preferences = { ...preferences, timezone };
      }
      yield call(Services.user.patchUserDetails, { preferences });
      const user = yield call(Services.user.fetchUser);
      if (user?.error?.message !== AUTH_ERR_MSG.USER_SESSION_ORGANISATION) {
        yield put(setAuthenticated(true));
        yield put(fetchTermsAcceptanceSuccess(termsResponse));
      } else {
        yield put(fetchTermsAcceptanceFailure(user.error));
        yield put(fetchAuthFailure(user.error));
      }
    } else {
      yield put(fetchTermsAcceptanceFailure(termsResponse.error));
      yield put(initAppFailure());
    }
  } catch (err) {
    const errMsg = (err instanceof Error && err.stack) || err;
    yield put(fetchTermsAcceptanceFailure(errMsg));
    yield put(fetchAuthFailure(errMsg));
    yield put(initAppFailure());
  }
}
// Select session user Organisation role
function* handleUserSessionOrg(request?: any) {
  const payload = { organisationId: request?.payload };
  try {
    const selectOrgResponse = yield call(
      Services.auth.selectUserSessionOrganisationService,
      payload
    );
    if (selectOrgResponse) {
      yield call(Services.auth.refreshUserAuthToken);
      yield delay(3000);
      const gwtToken = localStorage.getItem(
        LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID
      );
      if (gwtToken) {
        const currentUser = yield call(Services.user.fetchUser);
        // Persist user before auth flags so multi-org redirect can read preferences.user_type
        yield put(setAppUserRole(currentUser?.role));
        yield put(setUserSession(currentUser));
        yield put(fetchUserSuccess(currentUser));
        yield put(
          fetchSelectSessionOrgRoleSuccess({
            ...selectOrgResponse,
            role: currentUser.role
          })
        );
        yield put(setAuthenticated(true));
        yield call(fetchAuthSuccess);
      }
    } else {
      yield put(fetchSelectSessionOrgRoleFailure(selectOrgResponse?.error));
    }
  } catch (err) {
    const errMsg = (err instanceof Error && err.stack) || err;
    yield put(fetchSelectSessionOrgRoleFailure(errMsg));
    yield put(fetchAuthFailure(errMsg));
  }
}
// Watchers
function* watchLoginRequest() {
  yield takeLatest(LoginActionTypes.FETCH_REQUEST, handleLogin);
}
function* watchUserTermsRequest() {
  yield takeLatest(LoginActionTypes.ACCEPT_SIGNIN_TERMS, handleUserTerms);
}
function* watchUserSessionOrg() {
  yield takeLatest(
    LoginActionTypes.SELECT_SESSION_ORG_ROLE,
    handleUserSessionOrg
  );
}

function* doFetchOTPCode(request: any) {
  try {
    const otpUser = yield call(Services.auth.fetchOTPUsername, request.payload);

    if (otpUser.error) {
      return yield put(fetchOTPCodeFailure('User does not exist'));
    }
    const otpResponse = yield call(
      Services.auth.fetchOTPCode,
      otpUser.username
    );
    yield put(fetchOTPCodeSuccess(otpResponse));
  } catch (err) {
    console.log(err);
    yield put(fetchOTPCodeFailure(true));
  }
}

function* doSignInOTPCode(request: any) {
  try {
    const congnigto = yield call(
      Services.auth.SignInWithOTPCode,
      request.payload
    );
    if (_.isUndefined(congnigto.challengeParam)) {
      yield put(signInWithOTPCodeSuccess(''));
    } else {
      yield put(signInWithOTPCodeFailure('notauthorizedexception'));
    }
  } catch (err) {
    console.log(err);
    yield put(signInWithOTPCodeFailure('timeoutexception'));
  }
}

function* doSignInExternalApp(request: any) {
  try {
    const congnigto = yield call(
      Services.auth.SignInWithExternalApp,
      request.payload
    );

    if (_.isUndefined(congnigto.challengeParam)) {
      yield put(signInForExternalAppSuccess(''));
    } else {
      yield put(signInForExternalAppFailure('notauthorizedexception'));
    }
  } catch (err) {
    console.log(err);
    yield put(signInForExternalAppFailure('timeoutexception'));
  }
}

function* doSignInWithTemporaryCode(request: any) {
  try {
    //If user sign in before, skip sign in process
    const getAppState = (state: any) => state.app;
    const appState = yield select(getAppState);
    const auth = yield call(Services.auth.fetchCurrentUser, appState.loginType);
    if (auth?.user) {
      if (auth.user.username === request.payload.username) {
        const user = yield call(Services.user.fetchUser);
        const timezone = Utils.getTimeZone();
        if (!user?.error) {
          if (!user.preferences?.timezone) {
            const preferences = { ...user?.preferences, timezone };
            yield call(Services.user.patchUserDetails, { preferences });
          }
          if (appState.isSignOutCompleted) {
            yield put(setIsSignOutCompleted(false));
          }
          yield put(setAppUserRole(user?.role));
          yield put(setUserSession(user));
          yield put(fetchUserSuccess(user));
          yield put(signInWithTemporaryCodeSuccess(''));
          return;
        }
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.AWS_SIGNIN_HOSTED_UI);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID);
        yield call(Services.auth.clearCognitoLocalStorage);
        yield put(resetAppAuthStateFinish());
        yield put(endSession());
      }
    }
  } catch (err) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.AWS_SIGNIN_HOSTED_UI);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID);
    yield call(Services.auth.clearCognitoLocalStorage);
    yield put(resetAppAuthStateFinish());
    yield put(endSession());
  }

  try {
    const congnigto = yield call(
      Services.auth.SignInWithTemporaryCode,
      request.payload
    );
    if (_.isUndefined(congnigto.challengeParam)) {
      yield put(signInWithTemporaryCodeSuccess(''));
    } else {
      yield put(signInWithTemporaryCodeFailure('notauthorizedexception'));
    }
  } catch (err) {
    console.log(err);
    yield put(signInWithTemporaryCodeFailure('timeoutexception'));
  }
}

export function* onFetchOTPCode() {
  yield takeLatest(LoginActionTypes.FETCH_OTP_CODE, doFetchOTPCode);
}

export function* onSignInWithOTPCode() {
  yield takeLatest(LoginActionTypes.SIGN_IN_WITH_OTP_CODE, doSignInOTPCode);
}

export function* onSignInWithTemporaryCode() {
  yield takeLatest(
    LoginActionTypes.SIGN_IN_WITH_TEMPORARY_CODE,
    doSignInWithTemporaryCode
  );
}

export function* onSignInWithExternalApp() {
  yield takeLatest(
    LoginActionTypes.SIGN_IN_FOR_EXTERNAL_APP,
    doSignInExternalApp
  );
}

function* loginSagas() {
  yield all([
    call(watchLoginRequest),
    call(watchUserTermsRequest),
    call(watchUserSessionOrg),
    call(onFetchOTPCode),
    call(onSignInWithOTPCode),
    call(onSignInWithTemporaryCode),
    call(onSignInWithExternalApp)
  ]);
}
export default loginSagas;
